import { track } from '@amplitude/marketing-analytics-browser'
import isEmpty from 'lodash.isempty'
import uniqBy from 'lodash.uniqby'
import { useEffect, useState } from 'react'
import { useScrollDirection } from 'react-use-scroll-direction'
import styled from 'styled-components'

import { fetchPostsPreview, fetchGridPreviewByAccount } from 'api'
import type {
  AccountSelectorProps,
  UpcomingPostCardProps,
  ConversationProps,
  PostActionProps,
} from 'Components'
import { Navbar, LoadingPlaceholder, EmptyUpcomingPosts } from 'Components'
import Dropdown from 'Components/Common/Dropdown'
import PoweredBySked from 'Components/Common/PoweredBySked'
import { Title, FlexAlignCenter, Desc } from 'Components/CommonStyleComponents'
import MobilePreviewGrid from 'Components/Mobile/MobilePreviewGrid'
import MobileUpcomingPostCard from 'Components/Mobile/MobileUpcomingPostCard'
import {
  datePlaceholderForDraftPost,
  normalizeGridPreviewAllPosts,
  normalizeSkedPreviewPost,
} from 'normalizer'
import getInstagramAccountOption from 'utils/getInstagramAccountOption'

const Container = styled.div`
  margin: 10px;
`

const MobileUpcomingPostCardWrap = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? 'block' : 'none')};
`

const NavbarStyled = styled(Navbar)`
  &&& {
    margin: -10px;
    padding: 0 10px;
    height: 65px;

    > div img {
      width: 32px;
      margin-right: 14px;
    }
  }
`

const UpcomingPostTitle = styled(Title)`
  margin: 26px auto 16px;
  text-align: center;
`

const PostGridPreviewTab = styled(FlexAlignCenter)<{ isVisible: boolean }>`
  margin: 0 -10px 20px;
  justify-content: space-around;
  border-bottom: 1px solid #c4c4c4;
  position: sticky;
  top: 0;
  z-index: 3;
  background: #ffffff;
  padding: 10px;
  transition: all 0.1s ease-in;
  ${(props) =>
    props.isVisible
      ? 'visibility: visible; opacity: 1'
      : 'visibility: hidden; opacity: 0'};

  > div.tab {
    flex: 1;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 5px;
    cursor: pointer;

    &.active {
      color: #4f31ff;
      opacity: 1;
    }

    &.disabled {
      pointer-events: none;
    }
  }
`

const PostOrGridViewTabActive = styled.div<{ isAlignRight: boolean }>`
  height: 5px;
  border-radius: 5px;
  position: absolute;
  width: 50%;
  background: #4f31ff;
  bottom: -3px;
  left: 0;

  ${(props) =>
    props.isAlignRight &&
    `
    left: auto;
    right: 0;
  `}
`

const UpcomingPostCardWrap = styled.div`
  .date {
    font-size: 16px;
    font-weight: 700;
    margin: 28px auto 18px 12px;
    color: #454545;

    &.hidden {
      display: none;
    }
  }
`

export const PendingCountWrap = styled.span<{ isDraftPost: boolean }>`
  color: #4f31ff;
  margin-left: ${(props) => (props.isDraftPost ? '0px' : '8px')};

  .bullet {
    color: #454545;
  }
`

const PoweredBySkedStyled = styled(PoweredBySked)`
  &&& {
    display: flex;
    place-content: center;
    margin: 20px 0;
  }
`

const InstagramOnlyLabel = styled(Desc)`
  display: flex;
  justify-content: center;
  margin: 18px auto;
  font-weight: 500;
`

const MobilePreviewGridWrap = styled.div`
  display: grid;
  place-content: center;
  margin: 18px auto;
`

const CalendarNotice = styled.div`
  font-size: 16px;
  font-weight: 700;
  padding: 12px 14px;
  background: #f6f5ff;
  border: 1px solid #4f31ff;
  border-radius: 5px;
  margin-bottom: -14px;
`

interface AppMobileProps {
  isLoading: boolean
  setting: {
    logoUrl: string
    companyName: string
  }
  accountSelectorProps: AccountSelectorProps
  upcomingPosts: {
    id: string
    date: string
    isVisible: boolean
    posts: (UpcomingPostCardProps & {
      isVisible?: boolean
      postIndex: number
    })[]
  }[]
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
  accountsRaw: any
  conversationProps: ConversationProps
  postActionProps: PostActionProps
  reviewKey: string
  postId: string
  fetchComment?: (reviewKey: string, postId: string) => void
  isReadOnly: boolean
}

type PostOrGridView = 'postView' | 'gridView'

const AppMobile = (props: AppMobileProps) => {
  const {
    isLoading,
    setting: { logoUrl, companyName },
    accountSelectorProps,
    upcomingPosts,
    accountsRaw, // to build skedPreviewPosts
    conversationProps,
    postActionProps,
    reviewKey,
    postId,
    isReadOnly,
    updatePostState,
  } = props

  const [postOrGridView, setPostOrGridView] =
    useState<PostOrGridView>('postView')

  const [skedPreviewsPosts, setSkedPreviewsPosts] = useState<any[]>([])

  const [gridData, setGridData] = useState([])
  const [gridDataCached, setGridDataCached] = useState([])

  const [isSkedPreviewLoading, setIsSkedPreviewLoading] = useState(false)
  const [isGridPreviewLoading, setIsGridPreviewLoading] = useState(false)

  const handlePostGridTabClick = (v: PostOrGridView) => () => {
    isGridPreviewLoading ? alert('please wait...') : setPostOrGridView(v)

    track('ext-planner-tab-click')
  }

  const [showStickyTab, setShowStickyTab] = useState(true)
  const { isScrollingUp, isScrollingDown } = useScrollDirection()
  useEffect(() => {
    if (window.scrollY >= 130) {
      isScrollingUp && setShowStickyTab(true)
      isScrollingDown && setShowStickyTab(false)
    } else {
      setShowStickyTab(true)
    }
  }, [isScrollingUp, isScrollingDown])

  useEffect(() => {
    const fetchPostsPreviewAsync = async () => {
      setIsSkedPreviewLoading(true)
      const skedPreviewPostsResponse = await fetchPostsPreview(reviewKey)

      const skedPreviewsPosts = skedPreviewPostsResponse.map(
        (skedPreviewResponse: any) =>
          normalizeSkedPreviewPost(skedPreviewResponse, accountsRaw)
      )

      setSkedPreviewsPosts(skedPreviewsPosts)
      setIsSkedPreviewLoading(false)
    }

    if (!isLoading) {
      fetchPostsPreviewAsync()
    }
  }, [isLoading])

  const accountDropdownOption = getInstagramAccountOption(
    accountSelectorProps.accounts
  )

  const accountIds = accountDropdownOption.map((acc) => acc.id)

  useEffect(() => {
    if (
      postOrGridView === 'gridView' &&
      isEmpty(gridDataCached) &&
      accountIds.length > 0
    ) {
      const fetchGridPreviewByAccountAsync = async (ids: string[]) => {
        setIsGridPreviewLoading(true)
        if (!ids || ids.length === 0) return []
        return await Promise.all(
          ids.map(async (id) => await fetchGridPreviewByAccount(reviewKey, id))
        )
      }

      fetchGridPreviewByAccountAsync(accountIds).then((posts) => {
        const gridPosts = uniqBy(posts.filter((p) => p.length).flat(1), 'id')

        if (gridPosts.length > 0) {
          setGridData(
            normalizeGridPreviewAllPosts(gridPosts).filter((post: any) =>
              post.accounts.some((id: string) => accountIds[0] === id)
            )
          )
          setGridDataCached(normalizeGridPreviewAllPosts(gridPosts))
        }

        setIsGridPreviewLoading(false)
      })
    }
  }, [postOrGridView, gridDataCached, accountIds])

  return (
    <Container>
      <NavbarStyled logo={logoUrl} name={companyName} />
      <UpcomingPostTitle>Upcoming Posts</UpcomingPostTitle>
      <PostGridPreviewTab
        isVisible={showStickyTab}
        data-testid="postGridPreviewTab"
      >
        <div
          className={`tab ${postOrGridView === 'postView' && 'active'}`}
          onClick={handlePostGridTabClick('postView')}
        >
          Post Preview
        </div>
        <div
          className={`tab ${postOrGridView === 'gridView' && 'active'} ${
            isLoading && 'disabled'
          }`}
          onClick={handlePostGridTabClick('gridView')}
        >
          Grid Preview
        </div>
        <PostOrGridViewTabActive isAlignRight={postOrGridView === 'gridView'} />
      </PostGridPreviewTab>

      {postOrGridView === 'postView' && isReadOnly && (
        <CalendarNotice data-testid="calendar-view-notification">
          For a better Calendar view, please view this link on a desktop device.
        </CalendarNotice>
      )}

      {!isLoading && !isGridPreviewLoading && postOrGridView === 'gridView' && (
        <>
          <InstagramOnlyLabel>Available for Instagram only</InstagramOnlyLabel>
          <Dropdown
            name="accounts"
            placeholder="Select from options"
            onChange={(selected) => {
              const selectedId = selected.value

              const nextGridData = gridDataCached.filter((post: any) =>
                post.accounts.some((id: string) => selectedId === id)
              )

              if (nextGridData.length > 0) {
                setGridData(nextGridData)
              } else {
                setGridData([])
              }
            }}
            options={accountDropdownOption}
          />
        </>
      )}
      {isLoading ? (
        <LoadingPlaceholder isMobile />
      ) : !isLoading &&
        !isSkedPreviewLoading &&
        isEmpty(upcomingPosts.flatMap((p) => p.posts)) ? (
        <div
          style={{
            marginTop: isReadOnly ? '30px' : 'auto',
          }}
        >
          <EmptyUpcomingPosts />
        </div>
      ) : (
        postOrGridView === 'postView' && (
          <>
            <UpcomingPostCardWrap>
              {upcomingPosts.map(
                ({ date, posts, id: postRowId, isVisible }) => {
                  const renderPendingCount = (
                    posts: UpcomingPostCardProps[]
                  ) => {
                    let pendingCount = 0
                    posts.forEach((post) => {
                      if (post.status === 'pending') {
                        pendingCount += 1
                      }
                    })
                    return pendingCount
                  }
                  return (
                    <div key={postRowId + date}>
                      <div className={`date ${isVisible ? '' : 'hidden'}`}>
                        {date && date === datePlaceholderForDraftPost
                          ? `Drafts`
                          : date}
                        <PendingCountWrap
                          isDraftPost={date === datePlaceholderForDraftPost}
                        >
                          {renderPendingCount(posts) > 0 && !isReadOnly && (
                            <>
                              <span className="bullet">
                                {date !== datePlaceholderForDraftPost && '•'}{' '}
                              </span>
                              {renderPendingCount(posts)} pending
                            </>
                          )}
                        </PendingCountWrap>
                      </div>
                      {posts.map((post) => {
                        const { id, status, platform, time } = post

                        const postMessages =
                          status === 'requestChange' &&
                          conversationProps.messages?.find(
                            (item) => item.postId === postId
                          )

                        const hasPostedComment =
                          postMessages &&
                          postMessages.messages
                            ?.filter((msg) => msg.isRejectedMessage)
                            .every((msg) => Boolean(msg.isRejectedComment))

                        return (
                          <MobileUpcomingPostCardWrap
                            isVisible={isVisible}
                            key={status + time + id}
                          >
                            <MobileUpcomingPostCard
                              id={id}
                              status={status}
                              platform={platform}
                              time={time}
                              when={post.when}
                              skedPreviewPost={skedPreviewsPosts.find(
                                (post) => post.id === id
                              )}
                              updatePostState={updatePostState}
                              conversation={{
                                isLoading: conversationProps.isLoading,
                                isSubmitting: conversationProps.isSubmitting,
                                messages: conversationProps.messages,
                                canReply: status === 'requestChange',
                                isReplied:
                                  hasPostedComment ||
                                  conversationProps?.isRepliedPosts?.includes(
                                    id
                                  ),
                                submitReply: conversationProps.submitReply,
                                reviewKey,
                                postId: id,
                                fetchComment: conversationProps.fetchComment,
                                isConversationsEnabled:
                                  conversationProps.isConversationsEnabled,
                                collaborators: conversationProps.collaborators,
                                isReadOnly,
                              }}
                              postAction={{
                                postId,
                                postStatus: status,
                                isLoading: postActionProps.isLoading,
                                isMobile: true,
                                reviewKey,
                                updatePostState,
                              }}
                              isReadOnly={isReadOnly}
                            />
                          </MobileUpcomingPostCardWrap>
                        )
                      })}
                    </div>
                  )
                }
              )}
            </UpcomingPostCardWrap>

            <PoweredBySkedStyled />
          </>
        )
      )}
      {postOrGridView === 'gridView' && (
        <MobilePreviewGridWrap>
          {isGridPreviewLoading ? (
            'fetching... please wait...'
          ) : isEmpty(gridData) ? (
            <>
              <br />
              We couldn&apos;t find any upcoming posts
            </>
          ) : (
            <MobilePreviewGrid posts={gridData} isReadOnly={isReadOnly} />
          )}
        </MobilePreviewGridWrap>
      )}
    </Container>
  )
}

export default AppMobile
